import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api";
import { Heart, ArrowLeft, Gift, ExternalLink } from "lucide-react";

const PACKAGES = [
  { amount: 25, label: "1 Kid", desc: "Cover one keiki's entry" },
  { amount: 50, label: "2 Kids", desc: "Cover two entries" },
  { amount: 75, label: "3 Kids", desc: "Cover three entries" },
];

const WISHLIST_ITEMS = [
  "5-gallon buckets & potting soil (bucket garden)", "Gloves, trowels & seedlings",
  "Reef-safe sunscreen & first aid kits", "Water jugs, coolers & ice",
  "Hi-vis parking vests & work gloves", "Trash bags, grabbers & recycling bags",
  "Keiki zone games, prizes & face paint", "Zip ties, duct tape, bungees & tarps",
  "Portable power banks & batteries", "Air horn, whistles & heat flags",
];

export default function Sponsor() {
  const [fund, setFund] = useState(null);
  const [wishlist, setWishlist] = useState("");
  const [bluevineUrl, setBluevineUrl] = useState("");
  const [amount, setAmount] = useState(25);
  const [went, setWent] = useState(false);

  useEffect(() => {
    api.sponsorshipStatus().then(setFund).catch(() => {});
    api.config().then((c) => {
      setWishlist(c.amazon_wishlist_url || "");
      setBluevineUrl(c.bluevine_url || "");
    }).catch(() => {});
  }, []);

  const donate = () => {
    if (!bluevineUrl) return;
    window.open(bluevineUrl, "_blank", "noopener,noreferrer");
    setWent(true);
  };

  return (
    <div className="max-w-lg mx-auto p-4 pb-28" data-testid="sponsor-view">
      <Link to="/register" className="font-display text-xs uppercase tracking-widest text-gray-500 flex items-center gap-1 mb-3"><ArrowLeft size={14} /> Back to Register</Link>
      <h1 className="font-display text-4xl font-black uppercase tracking-tight mb-1">Sponsor a Kid</h1>
      <p className="font-display text-gray-500 uppercase text-xs tracking-widest mb-5">Help a keiki compete at Pounders Fest</p>

      <div className="brutal p-4 mb-5">
        <p className="font-display text-gray-700">
          Not every ohana can cover an entry. Chip in and we'll put your sponsorship toward a young competitor's entry fee. Every bit helps grow the next generation of riders. <strong>Mahalo!</strong>
        </p>
      </div>

      {fund != null && (() => {
        const raised = Number(fund.total_donated) || 0;
        const kidsCovered = Math.round((Number(fund.total_allocated) || 0) / 25);
        const waiting = Number(fund.applicants) || 0;
        const goal = raised + waiting * 25;
        const pct = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : (raised > 0 ? 100 : 0);
        return (
          <div className="brutal p-4 mb-5" data-testid="sponsor-progress">
            <div className="flex items-end justify-between mb-2 flex-wrap gap-1">
              <div className="font-display font-black uppercase tracking-tight text-lg">🌺 Fund a Keiki</div>
              <div className="font-display text-xs uppercase tracking-widest text-gray-500">
                <span className="font-score text-2xl text-green-700 align-middle">${raised.toFixed(0)}</span> raised
              </div>
            </div>
            <div className="brutal-2 h-6 w-full bg-white overflow-hidden" data-testid="sponsor-progress-track">
              <div className="h-full bg-green-600 transition-[width] duration-700 ease-out" style={{ width: `${pct}%` }} data-testid="sponsor-progress-bar" />
            </div>
            <div className="flex items-center justify-between mt-2 font-display text-xs uppercase tracking-widest text-gray-600 flex-wrap gap-1">
              <span data-testid="sponsor-kids-covered">{kidsCovered} keiki covered</span>
              {waiting > 0
                ? <span className="text-[#E63946] font-bold">{waiting} still waiting</span>
                : <span className="text-green-700 font-bold">Every applicant covered — mahalo!</span>}
            </div>
          </div>
        );
      })()}

      {fund != null && fund.pending_requests > 0 && (
        <div className="brutal-2 bg-[#E63946] text-white p-3 mb-5 text-center" data-testid="sponsor-waiting-nudge">
          <p className="font-display font-black uppercase tracking-tight">
            🙋 {fund.pending_requests} keiki {fund.pending_requests > 1 ? "are" : "is"} waiting for a sponsor right now
          </p>
          <p className="font-display text-xs mt-1">Your donation goes straight toward their entry. Mahalo for helping them hit the water.</p>
        </div>
      )}

      {/* Amount picker */}
      <div className="brutal p-4 mb-5" data-testid="sponsor-donate">
        <h2 className="font-display text-xl font-black uppercase tracking-tight mb-1">Choose an Amount</h2>
        <p className="font-display text-xs text-gray-500 mb-3">$25 covers one keiki's entry. Pick a suggested amount or enter your own.</p>
        <div className="grid grid-cols-3 gap-2 mb-3" data-testid="sponsor-packages">
          {PACKAGES.map((p) => (
            <button
              key={p.amount}
              data-testid={`sponsor-pkg-${p.amount}`}
              onClick={() => setAmount(p.amount)}
              className={`brutal-2 p-3 text-center active:scale-[0.98] transition-transform ${amount === p.amount ? "bg-black text-white" : "bg-white"}`}
            >
              <div className="font-score text-3xl">${p.amount}</div>
              <div className={`font-display text-[10px] uppercase tracking-widest ${amount === p.amount ? "text-gray-300" : "text-gray-500"}`}>{p.label}</div>
            </button>
          ))}
        </div>
        <label className="font-display text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Or enter a custom amount ($)</label>
        <input
          data-testid="sponsor-custom-amount"
          type="number"
          min="1"
          step="1"
          value={amount}
          onChange={(e) => setAmount(Math.max(0, Number(e.target.value)))}
          className="w-full brutal-2 p-3 font-display focus:outline-none bg-white mb-3"
        />
        {bluevineUrl ? (
          <button
            data-testid="sponsor-donate-btn"
            onClick={donate}
            disabled={!(amount > 0)}
            className="w-full brutal bg-[#E63946] text-white font-display font-black uppercase tracking-widest py-4 flex items-center justify-center gap-2 active:scale-[0.99] transition-transform disabled:opacity-60"
          >
            <Heart size={18} /> Donate ${Number(amount || 0).toFixed(0)} via Bluevine <ExternalLink size={16} />
          </button>
        ) : (
          <p className="font-display text-sm text-[#E63946]">Donation link unavailable — please contact the organizers.</p>
        )}
        <div className="brutal-2 bg-[#FFD700] p-3 mt-3 text-left" data-testid="sponsor-instructions">
          <p className="font-display text-sm font-bold uppercase tracking-wide">On the payment page, enter ${Number(amount || 0).toFixed(0)}</p>
          <p className="font-display text-sm mt-1">
            The Bluevine page won't know your amount, so please type <strong>${Number(amount || 0).toFixed(0)}</strong> yourself. Mahalo — every dollar puts a keiki in the water! 🤙
          </p>
        </div>
        {went && (
          <div className="brutal-2 bg-green-600 text-white p-3 mt-3 text-center" data-testid="sponsor-thanks">
            <p className="font-display font-black uppercase tracking-tight">Mahalo! 🌺</p>
            <p className="font-display text-xs mt-1">We opened the payment page in a new tab. Once your donation lands, it goes straight to a keiki's entry.</p>
          </div>
        )}
      </div>

      {wishlist && (
        <div className="brutal p-4 mt-6" data-testid="gear-wishlist">
          <div className="flex items-center gap-2 mb-1">
            <Gift size={22} className="text-[#E63946]" />
            <h2 className="font-display text-xl font-black uppercase tracking-tight">Gear Wishlist</h2>
          </div>
          <p className="font-display text-sm text-gray-600 mb-3">
            Rather give supplies? Buy something off our Amazon wishlist and it ships straight to us. Here's what we need most:
          </p>
          <ul className="mb-4 space-y-1">
            {WISHLIST_ITEMS.map((it) => (
              <li key={it} className="font-display text-sm flex gap-2"><span className="text-[#E63946] font-black">•</span>{it}</li>
            ))}
          </ul>
          <a
            data-testid="wishlist-link"
            href={wishlist}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center brutal bg-[#FFD700] text-black font-display font-black uppercase tracking-widest py-4 active:scale-[0.99] transition-transform"
          >
            🎁 Shop Our Amazon Wishlist
          </a>
          <p className="font-display text-[11px] text-gray-400 text-center mt-2">Every item helps us leave Pounders better than we found it. Mahalo!</p>
        </div>
      )}
    </div>
  );
}
